import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import Modal from '../components/Modal.jsx';
import MediaLibraryModal from './MediaLibraryModal.jsx';
import { pagesApi } from '../api/sites.js';
import { apiError } from '../api/client.js';

// Page-level metadata that drives SEO + sharing on the published site.
// Distinct from the inspector (which edits the SELECTED block); this is
// for the Page itself.
export default function PageSettingsModal({ open, onClose, page, onSaved }) {
  const [slug, setSlug] = useState('');
  const [title, setTitle] = useState('');
  const [seoTitle, setSeoTitle] = useState('');
  const [seoDescription, setSeoDescription] = useState('');
  const [seoImage, setSeoImage] = useState('');
  const [isHome, setIsHome] = useState(false);
  const [busy, setBusy] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);

  useEffect(() => {
    if (!open || !page) return;
    setSlug(page.slug || '');
    setTitle(page.title || '');
    setSeoTitle(page.seo_title || '');
    setSeoDescription(page.seo_description || '');
    setSeoImage(page.seo_image || '');
    setIsHome(!!page.is_home);
  }, [open, page]);

  async function save() {
    if (!page) return;
    setBusy(true);
    try {
      const updated = await pagesApi.update(page.id, {
        slug: slug.trim() || page.slug,
        title: title.trim() || page.title,
        seo_title: seoTitle.trim() || null,
        seo_description: seoDescription.trim() || null,
        seo_image: seoImage.trim() || null,
        is_home: isHome,
      });
      onSaved?.(updated);
      toast.success('Page settings saved');
      onClose();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  }

  const previewTitle = (seoTitle || title || 'Page').slice(0, 70);
  const previewDescription = (seoDescription || '').slice(0, 160);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Page settings"
      wide
      footer={
        <>
          <button onClick={onClose} className="btn-ghost">Cancel</button>
          <button onClick={save} className="btn-primary" disabled={busy}>
            {busy ? 'Saving…' : 'Save settings'}
          </button>
        </>
      }
    >
      <div className="space-y-6">
        <section>
          <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-ink-500">
            Basics
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Title (in editor)</label>
              <input
                className="input"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>
            <div>
              <label className="label">Slug (URL path)</label>
              <input
                className="input font-mono text-xs"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
              />
            </div>
          </div>
          <label className="mt-4 inline-flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={isHome}
              onChange={(e) => setIsHome(e.target.checked)}
            />
            <span>Use as home page</span>
          </label>
        </section>

        <section>
          <h3 className="mb-1 text-xs font-semibold uppercase tracking-wider text-ink-500">
            SEO & sharing
          </h3>
          <p className="mb-3 text-xs text-ink-500">
            Used by search engines and rendered as Open Graph + Twitter card
            metadata when this page is shared.
          </p>
          <div className="space-y-3">
            <div>
              <label className="label">SEO title (max 70 chars)</label>
              <input
                className="input"
                maxLength={70}
                value={seoTitle}
                onChange={(e) => setSeoTitle(e.target.value)}
                placeholder={title || 'Page'}
              />
              <div className="mt-1 text-[10px] text-ink-400">
                {seoTitle.length}/70 · falls back to the page title
              </div>
            </div>
            <div>
              <label className="label">SEO description (max 160 chars)</label>
              <textarea
                className="input min-h-[64px]"
                maxLength={160}
                value={seoDescription}
                onChange={(e) => setSeoDescription(e.target.value)}
                placeholder="A short summary for search results and social previews."
              />
              <div className="mt-1 text-[10px] text-ink-400">
                {seoDescription.length}/160
              </div>
            </div>
            <div>
              <label className="label">Share image</label>
              <div className="flex items-center gap-2">
                <input
                  className="input flex-1 text-xs"
                  placeholder="https://… or pick from library"
                  value={seoImage}
                  onChange={(e) => setSeoImage(e.target.value)}
                />
                <button
                  type="button"
                  onClick={() => setLibraryOpen(true)}
                  className="btn-secondary h-9 px-2 text-xs"
                >
                  ▦ Library
                </button>
                {seoImage && (
                  <button
                    type="button"
                    onClick={() => setSeoImage('')}
                    className="btn-ghost h-9 px-2 text-xs text-ink-500"
                  >
                    Clear
                  </button>
                )}
              </div>
              {seoImage && (
                <div className="mt-2 overflow-hidden rounded-lg border border-ink-200 bg-ink-50 dark:border-ink-800 dark:bg-ink-950">
                  <img
                    src={seoImage}
                    alt=""
                    className="block max-h-32 w-full object-cover"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                </div>
              )}
            </div>
          </div>
        </section>

        <section>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-ink-500">
            Search preview
          </h3>
          <div className="rounded-lg border border-ink-200 p-4 dark:border-ink-800">
            <div className="text-[11px] text-emerald-700">{location.host || 'pubvora.com'}/s/…/{slug}</div>
            <div className="mt-1 text-base font-medium text-blue-700">
              {previewTitle || 'Untitled page'}
            </div>
            <div className="mt-1 text-xs text-ink-600 line-clamp-2">
              {previewDescription || (
                <span className="italic text-ink-400">
                  Add a description to see how it'll look in search.
                </span>
              )}
            </div>
          </div>
        </section>
      </div>

      <MediaLibraryModal
        open={libraryOpen}
        onClose={() => setLibraryOpen(false)}
        onSelect={(url) => setSeoImage(url)}
        currentUrl={seoImage}
      />
    </Modal>
  );
}
